import type { CarProfile, EngineLoopSample, OneShotSample } from './CarProfile';

/**
 * THE SAME CAR, BOUNCED TO ONE FILE.
 *
 * A car here plays four ladders of loops at once - exhaust, cabin, the simulator's own bank and a
 * fourth microphone - each crossfading between its own rpm rungs, each on its own fader. That is a
 * lot of files to fetch, a lot to decode and a lot of voices running for a sound the driver can no
 * longer change once the mix is settled. `tools/single-file.py` renders exactly what those four
 * banks produce, at the mix the car ships with, over a grid of rpm and lays the results end to end
 * in ONE file; each rung is a loop region inside it.
 *
 * What that buys: one download, one decode, one ladder. What it costs: nothing at a rung, and
 * between two rungs the crossfade is of two bounces rather than of each bank's own neighbours -
 * equal-power either way, on a grid finer than any single bank's ladder.
 *
 * THIS IS THE CAR NOW (Marc, 2026-09-01: "ya hacer principal los single files y llamarlos normal").
 * The bounce carries the plain name and the plain slug; the four-bank build it was rendered from
 * stays in the line-up under `-4v`, admin-only, as the reference to A/B against and the thing the
 * SOLO buttons are for.
 *
 * The one-shots are the original car's own files, played from the same folder: they are events, not
 * layers, and nothing is gained by copying them. The two that ARE layered - the limiter bed and the
 * launch bed, recorded from every microphone the mod has - are bounced too, or a single-bus car
 * would play the exhaust take alone exactly where the engine is loudest.
 */
export interface SingleFileBank {
  /** the car's own identity: the bounce is the product, the multi-bank build is the reference copy */
  id: string;
  slug: string;
  name: string;
  /** a bounce that REPLACES a shipped car is not a test car: it keeps that car's own shelf */
  adminOnly?: boolean;
  on: EngineLoopSample[];
  mid: EngineLoopSample[];
  off: EngineLoopSample[];
  /** the reciprocal of the render's headroom scale, so the bounce comes back to its own level */
  baseGain: number;
  /** bounced beds, when the car had companions to bounce */
  limiter?: OneShotSample;
  launch?: OneShotSample;
}

export function asSingleFile(base: CarProfile, bank: SingleFileBank): CarProfile {
  return {
    ...base,
    // A bounce WITH a mid ladder carries the floor inside its renders (they are the car's own load
    // mix at full, half and shut throttle), so its own mix must not add the floor again. One WITHOUT
    // a mid ladder is two pure ladders and keeps the car's own floor, which makes it exact at every
    // pedal position instead of only at the ones that were rendered.
    audio: { ...base.audio, onMixFloor: bank.mid.length ? 0 : base.audio.onMixFloor },
    id: bank.id,
    slug: bank.slug,
    name: bank.name,
    available: true,
    adminOnly: bank.adminOnly ?? true,
    samples: {
      ...base.samples,
      baseGain: bank.baseGain,
      on: bank.on,
      mid: bank.mid,
      off: bank.off,
      // the four banks are now one
      overlay: undefined,
      sim: undefined,
      extra: undefined,
      // ...so their copies of the beds have no bus to ride, and the bounced ones take their place
      limiterCabin: undefined,
      limiterExtra: undefined,
      launchCabin: undefined,
      launchExtra: undefined,
      limiter: bank.limiter ?? base.samples.limiter,
      launch: bank.launch ?? base.samples.launch,
    },
  };
}
